/*
 * Full Width Image Banner slider.
 *
 * Cross-fades between "slide" blocks and, optionally, pushes the colors declared
 * on the active slide up to the header (logo, mega menu links, icons, background).
 * The header lives outside of this section, so the colors are handed over as
 * custom properties on <html> plus a marker class per property. The matching
 * `html.fwb-hc-* { ... !important }` rules are printed by the section itself, so
 * nothing here needs to know how the header is built.
 */
(function () {
  if (customElements.get('full-width-banner')) return;

  var HEADER_COLORS = [
    { key: 'logoColor', property: '--fwb-logo-color', className: 'fwb-hc-logo' },
    { key: 'menuColor', property: '--fwb-menu-color', className: 'fwb-hc-menu' },
    { key: 'menuHoverColor', property: '--fwb-menu-hover-color', className: 'fwb-hc-menu-hover' },
    { key: 'iconsColor', property: '--fwb-icons-color', className: 'fwb-hc-icons' },
    { key: 'headerBackground', property: '--fwb-header-bg', className: 'fwb-hc-bg' },
  ];

  class FullWidthBanner extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.full-width-image__slide'));
      if (this.slides.length === 0) return;

      this.dots = Array.from(this.querySelectorAll('.full-width-image__dot'));
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
      this.headerMode = this.dataset.headerColors || 'off';
      this.paused = false;

      this.bindControls();
      this.bindSwipe();
      this.setSlide(this.index, true);

      if (this.headerMode === 'always') {
        this.applyHeaderColors();
      } else if (this.headerMode === 'while_visible') {
        this.observeViewport();
      }

      this.onVisibilityChange = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());

      this.bindThemeEditor();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.releaseHeaderColors();
      if (this.observer) this.observer.disconnect();
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    bindControls() {
      var prev = this.querySelector('.full-width-image__arrow--prev');
      var next = this.querySelector('.full-width-image__arrow--next');

      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.setSlide(index);
          this.startAutoplay();
        });
      });
    }

    bindSwipe() {
      if (this.slides.length < 2) return;

      var startX = null;

      this.addEventListener(
        'touchstart',
        (event) => {
          startX = event.changedTouches[0].clientX;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          var deltaX = event.changedTouches[0].clientX - startX;
          startX = null;
          if (Math.abs(deltaX) < 40) return;
          this.step(deltaX < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    bindThemeEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        var index = this.slides.indexOf(event.target.closest('.full-width-image__slide'));
        if (index === -1) return;
        this.paused = true;
        this.stopAutoplay();
        this.setSlide(index);
      });

      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.contains(event.target)) return;
        this.paused = false;
        this.startAutoplay();
      });
    }

    step(direction) {
      var count = this.slides.length;
      this.setSlide((this.index + direction + count) % count);
      this.startAutoplay();
    }

    setSlide(index, initial) {
      this.index = index;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      if (!initial || this.headerMode === 'always') this.applyHeaderColorsIfOwner();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.paused || this.slides.length < 2 || this.dataset.autoplay !== 'true' || document.hidden) return;
      this.timer = setInterval(() => this.step(1), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    observeViewport() {
      if (!('IntersectionObserver' in window)) {
        this.applyHeaderColors();
        return;
      }

      // Only the band right under the header counts as "visible", so the colors
      // hand over exactly while the banner sits behind the header.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.applyHeaderColors();
            else this.releaseHeaderColors();
          });
        },
        { rootMargin: '0px 0px -85% 0px', threshold: 0 }
      );

      this.observer.observe(this);
    }

    applyHeaderColorsIfOwner() {
      if (window.fullWidthBannerHeaderOwner === this) this.applyHeaderColors();
    }

    applyHeaderColors() {
      if (this.headerMode === 'off') return;

      var slide = this.slides[this.index];
      if (!slide) return;

      window.fullWidthBannerHeaderOwner = this;
      var root = document.documentElement;

      HEADER_COLORS.forEach((color) => {
        var value = slide.dataset[color.key];
        if (value) {
          root.style.setProperty(color.property, value);
          root.classList.add(color.className);
        } else {
          root.style.removeProperty(color.property);
          root.classList.remove(color.className);
        }
      });
    }

    releaseHeaderColors() {
      if (window.fullWidthBannerHeaderOwner !== this) return;

      window.fullWidthBannerHeaderOwner = null;
      var root = document.documentElement;

      HEADER_COLORS.forEach((color) => {
        root.style.removeProperty(color.property);
        root.classList.remove(color.className);
      });
    }
  }

  customElements.define('full-width-banner', FullWidthBanner);
})();
